/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Skeleton } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'

// Mirrors the loaded layout: a summary card, then the Roster / Roster Spares /
// Eligible Game Spares sections. Row counts are indicative only — they keep the
// placeholder a similar height to real content so the modal doesn't jump when
// the data lands.
const SECTION_ROW_COUNTS = [4, 2, 2]

/**
 * Loading placeholder for the Manage Team Attendance modal. Shaped like the
 * real content rather than a bare "Loading…" line, so the modal's size and
 * structure stay stable while the roster request is in flight.
 */
const AttendanceSkeleton: React.FC = () => (
  <div css={containerStyle} data-testid='attendance-skeleton'>
    <div css={statusCardStyle}>
      <Skeleton active title={false} paragraph={{ rows: 2 }} />
    </div>

    {SECTION_ROW_COUNTS.map((rowCount, sectionIndex) => (
      <div key={sectionIndex} css={sectionStyle}>
        <div css={sectionHeaderStyle}>
          <Skeleton.Input active size='small' css={sectionLabelStyle} />
        </div>
        {Array.from({ length: rowCount }, (_, rowIndex) => (
          <div
            key={rowIndex}
            css={[rowStyle, rowIndex % 2 === 1 ? oddRowStyle : evenRowStyle]}
          >
            <Skeleton.Avatar active size={32} shape='circle' />
            <Skeleton active title={false} paragraph={{ rows: 1 }} />
          </div>
        ))}
      </div>
    ))}
  </div>
)

export default AttendanceSkeleton

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px 16px;
`

const statusCardStyle = css`
  padding: 12px;
  border-radius: 8px;
  background-color: ${colors.SECONDARY_400};
`

const sectionStyle = css`
  display: flex;
  flex-direction: column;
`

const sectionHeaderStyle = css`
  padding: 12px 8px 8px;
`

/* Roughly the width of a real section label ("Roster Spares (2)"). Ant sizes
   Skeleton.Input from its own props, so override the rendered element. */
const sectionLabelStyle = css`
  &.ant-skeleton .ant-skeleton-input {
    min-width: 140px;
    width: 140px;
  }
`

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px;
`

/* Same striping as the DataGrid theme (first row SECONDARY_300, then
   SECONDARY_400), so the placeholder reads as the grid it is standing in for. */
const evenRowStyle = css`
  background-color: ${colors.SECONDARY_300};
`

const oddRowStyle = css`
  background-color: ${colors.SECONDARY_400};
`
